import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";

import {
    Shield,
    Key,
    Clock,
    Calendar,
    Search,
    User,
    Settings,
    FileText,
    CheckCircle2,
    Loader2
} from "lucide-react";
import useUserStore from "../../../services/userStore";
import { Device } from "../interfaces/EndpointInterfaces";
import { submitForm } from "./Api/tenantuserendpointsapi";
import { useToast } from "../../../hooks/use-toast";

interface RequestConnectAccessDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmitSuccess?: () => void | Promise<void>;
    deviceName?: string;
    applicationName?: string;
    osType?: "windows" | "linux" | "manual";
    userList?: any[];
    sessionRequest?: boolean | false;
    resourceType?: string;
    resourcseId?: string;
    isWhitewanAccess?: string;
    isFromEndPoint?: boolean | false;
    isFromEndPointApp?: boolean | false;
    tenantName: string;
    device?: Device | null;
    publisherId: string;
    resourceName: string;
    resourcePath: string;
}

interface Credential {
    id: string;
    name: string;
    email: string;
    credentialLabel: string;
    credentialUsername: string;
}

export default function RequestConnectAccessDialog({
    open,
    onOpenChange,
    onSubmitSuccess,
    deviceName = "",
    applicationName = "",
    osType = "windows",
    userList,
    sessionRequest,
    resourceType,
    resourcseId,
    isWhitewanAccess,
    isFromEndPoint,
    isFromEndPointApp,
    tenantName,
    device,
    publisherId,
    resourceName,
    resourcePath
}: RequestConnectAccessDialogProps) {
    const [connectionMode, setConnectionMode] = useState("generic");
    const [accessType, setAccessType] = useState("DYNAMIC");
    const [scheduleAccess, setScheduleAccess] = useState(false);
    const [justification, setJustification] = useState("");
    const [timeMinutes, setTimeMinutes] = useState("");
    const [selectedCredential, setSelectedCredential] = useState<Credential>();
    const [step, setStep] = useState<"credentials" | "request">("request");
    const [credentialSearch, setCredentialSearch] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    let { email, role, userRole, userId } = useUserStore((state) => state.user);
    const { toast } = useToast();

    const handleConnectionModeChange = (value: string) => {
        setConnectionMode(value);
        if (value === "existingCredentials" && !selectedCredential) {
            setStep("credentials");
        } else {
            setStep("request");
        }
    };

    const isDeviceSSh = (device: Device) => {
        return (
            !device?.isManuallyAddedMachine &&
            (
                device?.OperatingSystem?.toLowerCase().includes("linux") ||
                device?.OperatingSystem?.toLowerCase().includes("macos")
            )
        );
    };

    useEffect(() => {
        if (open && (osType === "linux" || osType === "manual")) {
            setConnectionMode("existingCredentials");
            setStep("credentials");
        }

    }, [osType, open]);


    useEffect(() => {
        if (userList)
            if (userList?.length > 0) {
                const creds = userList.map(device => ({
                    id: device.id,
                    name: device.cn,
                    email: device.email,
                    credentialUsername: device.credentialUsername || "",
                    credentialLabel: device.credentialLabel || ""
                }));
                console.log(creds)
                setCredentials(creds || []);
            }
    }, [userList])


    const handleCredentialSelect = (credential: Credential) => {
        setSelectedCredential(credential);
        setStep("request");
    };

    const getStringBeforeAtSymbol = (str: string) => {
        const atIndex = str.indexOf("@");
        return atIndex !== -1 ? str.substring(0, atIndex) : str;
    };

    const isDeviceWindows = (device: Device): boolean => {
        if (device.OperatingSystem && device.OperatingSystem.trim().length > 0) {
            return device.OperatingSystem.toLowerCase().includes("windows");
        }
        return false;
    };

    const isServer = device?.belongedToUser
        ? device.belongedToUser === email
            ? false
            : device?.isServer
        : device?.isServer;

    const handleSubmit = async () => {
        if (isSubmitting || !device) {
            return;
        }

        setIsSubmitting(true);

        if (device) {
            const username = getStringBeforeAtSymbol(email);

            const scheduledStart = scheduleAccess && startDate ? new Date(startDate).toISOString() : null;
            const scheduledEnd = scheduleAccess && endDate ? new Date(endDate).toISOString() : null;

            const isConfiguredCredentialFlow = isDeviceWindows(device) || !!device?.isManuallyAddedMachine;

            const payload = {
                userId: sessionRequest ? userId : selectedCredential?.id,
                username: sessionRequest ? username : selectedCredential?.name,
                email:
                    isFromEndPointApp && isDeviceSSh(device)
                        ? email
                        : sessionRequest
                            ? email
                            : selectedCredential?.email,
                resourceType: sessionRequest ? "GROUP" : resourceType,
                resourceId: resourcseId,
                resourceName,
                computerName:
                    isFromEndPointApp && isDeviceSSh(device)
                        ? sessionRequest
                            ? email
                            : selectedCredential?.email
                        : device?.dNSHostName,
                friendlyName: device?.friendlyName,
                resourcePath: resourcePath || "",
                requestMessage: justification,
                requestStatus: "PENDING",
                selectedTime: accessType.toUpperCase() === "PERMANENT" ? 0 : scheduleAccess ? 0 : timeMinutes,
                computerId: device?.id,
                createdAt: new Date().toISOString(),
                privilegeLevel: accessType.toUpperCase(),
                tenantName,
                publisherId,
                userEmail: email,
                toConnect: sessionRequest ? true : false,
                isWhiteswanAccess: isWhitewanAccess,
                isServer,
                machineUser: selectedCredential?.name,
                machineUserId: selectedCredential?.id,
                connectionMode: isConfiguredCredentialFlow ? connectionMode : "",
                connectionUser: isConfiguredCredentialFlow ? selectedCredential?.credentialUsername : "",
                connectionLabel: isConfiguredCredentialFlow ? selectedCredential?.credentialLabel : "",
                type: "onPrem",
                timingMode: accessType.toUpperCase() === "PERMANENT" ? 0 : scheduleAccess ? "SCHEDULED" : "GENERIC",
                scheduledStart: accessType.toUpperCase() === "PERMANENT" ? null : scheduleAccess && scheduledStart,
                scheduledEnd: accessType.toUpperCase() === "PERMANENT" ? null : scheduleAccess && scheduledEnd,
            };

            try {
                await submitForm(payload);
                await onSubmitSuccess?.();

                toast({
                    title: "Request connect",
                    description: `Successfully raised the request for ${device.dNSHostName}`,
                });
            } catch (e: any) {
                toast({
                    title: "Request connect failed",
                    description:
                        e?.response?.data?.message ||
                        e?.message ||
                        `Unable to raise the request for ${device.dNSHostName}`,
                    variant: "destructive",
                });
                return;
            } finally {
                setIsSubmitting(false);
            }

            setCredentials([])
            onOpenChange(false);
            handleCancel()
        }

    };



    const handleBack = () => {
        if (step === "request" && connectionMode === "existingCredentials") {
            setStep("credentials");
        }
    };

    const handleCancel = () => {
        setSelectedCredential(undefined);
        onOpenChange(false);
        // Reset form
        setConnectionMode("generic");
        setAccessType("DYNAMIC");
        setScheduleAccess(false);
        setJustification("");
        setTimeMinutes("");
        setStep("request");
        setCredentialSearch("");
        setStartDate("");
        setEndDate("");
        setCredentials([])
    };

    useEffect(() => {
        if (!open) {
            setConnectionMode("generic");
            setAccessType("DYNAMIC");
            setScheduleAccess(false);
            setJustification("");
            setTimeMinutes("");
            setStep("request");
            setCredentialSearch("");
            setStartDate("");
            setEndDate("");
        }
    }, [open]);

    useEffect(() => {
        setSelectedCredential(undefined);
        setAccessType("DYNAMIC");
        setScheduleAccess(false);
        setJustification("");
        setTimeMinutes("");
        setCredentialSearch("");
        setStartDate("");
        setEndDate("");
    }, [connectionMode])


    useEffect(() => {
        if (accessType === "permanent") {
            setScheduleAccess(false);
            setStartDate("");
            setEndDate("");
        }
    }, [accessType]);


    // Filter credentials based on search
    const filteredCredentials = credentials?.filter(credential =>
        credential?.name?.toLowerCase().includes(credentialSearch.toLowerCase()) ||
        credential?.credentialUsername?.toLowerCase().includes(credentialSearch.toLowerCase())
    );

    // Show credentials selection step first if configured credentials is chosen
    if (step === "credentials" && connectionMode === "existingCredentials") {
        return (
            <Dialog open={open} onOpenChange={() => {
                setCredentials([])
                onOpenChange(false);
                handleCancel()
                onOpenChange(false)
            }} modal={true}>
                <DialogContent
                    className="max-w-xl max-h-[95vh] overflow-y-auto scrollbar-hide"
                    style={{ overscrollBehavior: "contain" }}
                >
                    <DialogHeader className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-semibold">Select Credentials</DialogTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Choose the credentials to use for secure connection to <span className="font-medium text-foreground">{deviceName}</span>
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Search Section */}
                        <Card>
                            <CardContent className="pt-6">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Search credentials by name, type, or description..."
                                        value={credentialSearch}
                                        onChange={(e) => setCredentialSearch(e.target.value)}
                                        className="pl-10 text-sm"
                                        data-testid="input-credential-search"
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Credentials List */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    Available Credentials ({filteredCredentials.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-80 overflow-y-auto">
                                    {filteredCredentials.map((credential) => (
                                        <div
                                            key={credential.id}
                                            className="p-4 border rounded-lg cursor-pointer hover-elevate transition-all duration-200 group"
                                            onClick={() => handleCredentialSelect(credential)}
                                            data-testid={`credential-${credential.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 flex items-center justify-center group-hover:scale-105 transition-transform">
                                                        <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-sm">{credential.name || credential.credentialUsername}</div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {filteredCredentials.length === 0 && (
                                    <div className="text-center py-8">
                                        <Search className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                                        <p className="text-sm text-muted-foreground">
                                            No credentials found matching your search.
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Try adjusting your search criteria.
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" className="px-6">
                                Cancel
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Main request form
    return (
        <Dialog open={open} onOpenChange={() => {
            setCredentials([])
            onOpenChange(false);
            handleCancel()
            onOpenChange(false)
        }} modal={true}>
            <DialogContent
                className="max-w-xl max-h-[95vh] overflow-y-auto scrollbar-hide"
                style={{ overscrollBehavior: "contain" }}
            >
                <DialogHeader className="space-y-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/30 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-semibold">
                                Request {osType === "linux" ? "Linux" : osType === "manual" ? "Manual Machine" : "Windows"} Access
                            </DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                Configure access for <span className="font-medium text-foreground">{applicationName}</span>
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {osType === "windows" && !device?.isManuallyAddedMachine && (
                        <div>
                            <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                                <Settings className="w-4 h-4" />
                                Credentials
                            </Label>
                            <RadioGroup
                                value={connectionMode}
                                onValueChange={handleConnectionModeChange}
                                className="grid grid-cols-3 gap-3"
                            >
                                <div className="relative">
                                    <RadioGroupItem value="generic" id="generic" className="peer sr-only" />
                                    <Label
                                        htmlFor="generic"
                                        className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                    >
                                        <User className="w-5 h-5 text-muted-foreground peer-data-[state=checked]:text-blue-600 mb-1" />
                                        <span className="font-medium text-xs">JIT User</span>
                                    </Label>
                                </div>
                                <div className="relative">
                                    <RadioGroupItem value="existingCredentials" id="existingCredentials" className="peer sr-only" />
                                    <Label
                                        htmlFor="existingCredentials"
                                        className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                    >
                                        <Key className="w-5 h-5 text-muted-foreground peer-data-[state=checked]:text-blue-600 mb-1" />
                                        <span className="font-medium text-xs text-center">Configured</span>
                                    </Label>
                                </div>
                                <div className="relative">
                                    <RadioGroupItem value="manualCredentials" id="manualCredentials" className="peer sr-only" />
                                    <Label
                                        htmlFor="manualCredentials"
                                        className="flex flex-col items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                    >
                                        <FileText className="w-5 h-5 text-muted-foreground peer-data-[state=checked]:text-blue-600 mb-1" />

                                        <span className="font-medium text-xs">Manual</span>
                                    </Label>
                                </div>
                            </RadioGroup>
                        </div>
                    )}

                    {/* Access Type */}
                    <div>
                        <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Access Type
                        </Label>
                        <RadioGroup value={accessType} onValueChange={setAccessType} className="grid grid-cols-2 gap-3">
                            <div className="relative">
                                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                                <Label
                                    htmlFor="DYNAMIC"
                                    className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                >
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                        <span className="font-medium text-sm">Timed</span>
                                    </div>
                                </Label>
                            </div>
                            <div className="relative">
                                <RadioGroupItem value="permanent" id="permanent" className="peer sr-only" />
                                <Label
                                    htmlFor="permanent"
                                    className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                >
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="font-medium text-sm">Permanent</span>
                                    </div>
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>

                    <Card>
                        <CardContent className="pt-3 space-y-3">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Application</Label>
                                    <div className="font-medium">{applicationName}</div>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Computer</Label>
                                    <div className="font-medium">{deviceName}</div>
                                </div>
                            </div>

                            {connectionMode === "existingCredentials" && selectedCredential && (
                                <div className="flex items-center gap-2 pt-2 border-t">
                                    <Key className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Credential: </span>
                                        <span className="font-medium">
                                            {selectedCredential.name || selectedCredential.credentialUsername}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {connectionMode === "manualCredentials" && (
                                <div className="flex items-center gap-2 pt-2 border-t">
                                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                    <div className="text-sm">
                                        <span className="text-muted-foreground">Method: </span>
                                        <span className="font-medium">Manual Entry</span>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {accessType === "DYNAMIC" && (
                        <div className="flex items-center justify-between">
                            <Label htmlFor="schedule-access" className="text-sm font-medium cursor-pointer">Schedule Access</Label>
                            <Switch
                                id="schedule-access"
                                checked={scheduleAccess}
                                onCheckedChange={setScheduleAccess}
                            />
                        </div>
                    )}


                    <Card>
                        <CardContent className="pt-3 space-y-3">
                            <div>
                                <Label htmlFor="justification" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Access Justification *
                                </Label>
                                <Textarea
                                    id="justification"
                                    value={justification}
                                    onChange={(e) => setJustification(e.target.value)}
                                    placeholder="Provide business justification for this access request..."
                                    className="min-h-[80px] resize-none"
                                    data-testid="textarea-justification"
                                />
                            </div>

                            {accessType === "DYNAMIC" && !scheduleAccess && (
                                <div>
                                    <Label htmlFor="time-minutes" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Duration (minutes) *
                                    </Label>
                                    <Input
                                        id="time-minutes"
                                        value={timeMinutes}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "") {
                                                setTimeMinutes("");
                                                return;
                                            }
                                            if (!/^\d+$/.test(value)) return;
                                            const num = Number(value);
                                            if (num >= 1 && num <= 999) {
                                                setTimeMinutes(value);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder="e.g., 60"
                                        type="number"
                                        min="1"
                                        max="999"
                                        className="max-w-xs"
                                        data-testid="input-time-minutes"
                                    />
                                </div>
                            )}

                            {scheduleAccess && (
                                <Card>
                                    <CardContent className="pt-3 space-y-3">
                                        <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Schedule Window *
                                        </Label>

                                        <div className="grid grid-cols-2 gap-3">
                                            {/* Start Date */}
                                            <div>
                                                <Label htmlFor="start-date" className="text-xs text-muted-foreground mb-1 block">
                                                    Start
                                                </Label>
                                                <Input
                                                    id="start-date"
                                                    type="datetime-local"
                                                    value={startDate}
                                                    min={(() => {
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        return today.toISOString().slice(0, 16);
                                                    })()}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        const selected = new Date(value);
                                                        setEndDate("");

                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);

                                                        if (selected < today) {
                                                            toast({
                                                                title: "Start Date/Time Selection Criteria",
                                                                description: "Start date/time cannot be before today.",
                                                                variant: "destructive",
                                                            });
                                                            return;
                                                        }

                                                        const maxAllowed = new Date();
                                                        maxAllowed.setMonth(today.getMonth() + 3);

                                                        if (selected > maxAllowed) {
                                                            toast({
                                                                title: "Start Date/Time Selection Criteria",
                                                                description: "Start date/time cannot be more than 3 months from today.",
                                                                variant: "destructive",
                                                            });
                                                            return;
                                                        }

                                                        setStartDate(value);

                                                    }}
                                                    className="appearance-auto bg-background border border-input text-foreground px-2 py-1.5 rounded-md w-full focus-visible:ring-2 focus-visible:ring-blue-500"
                                                    data-testid="input-start-date"
                                                />
                                            </div>

                                            {/* End Date */}
                                            <div>
                                                <Label htmlFor="end-date" className="text-xs text-muted-foreground mb-1 block">
                                                    End
                                                </Label>
                                                <Input
                                                    id="end-date"
                                                    type="datetime-local"
                                                    value={endDate}
                                                    disabled={!startDate}
                                                    min={startDate || new Date().toISOString().slice(0, 16)}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        const now = new Date();
                                                        const end = new Date(value);
                                                        const start = startDate ? new Date(startDate) : now;

                                                        if (end <= start) {
                                                            toast({
                                                                title: "End Date/Time Selection Criteria",
                                                                description: "End date/time must be later than the start date/time.",
                                                                variant: "destructive",
                                                            });
                                                            return;
                                                        }

                                                        if (end < now) {
                                                            toast({
                                                                title: "End Date/Time Selection Criteria",
                                                                description: "End date/time cannot be in the past.",
                                                                variant: "destructive",
                                                            });
                                                            return;
                                                        }

                                                        const maxAllowed = new Date(start);
                                                        maxAllowed.setDate(start.getDate() + 3);

                                                        if (end > maxAllowed) {
                                                            toast({
                                                                title: "End Date/Time Selection Criteria",
                                                                description: "End date/time cannot be more than 3 days after the start date/time.",
                                                                variant: "destructive",
                                                            });
                                                            return;
                                                        }

                                                        setEndDate(value);
                                                    }}

                                                    className="appearance-auto bg-background border border-input text-foreground px-2 py-1.5 rounded-md w-full focus-visible:ring-2 focus-visible:ring-blue-500"
                                                    data-testid="input-end-date"
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-3 border-t">
                        {connectionMode === "existingCredentials" && (
                            <Button variant="outline" onClick={handleBack} data-testid="button-back" size="sm">
                                Back
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" size="sm">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={
                                isSubmitting ||
                                !justification.trim() ||
                                (accessType === "DYNAMIC" &&
                                    ((!scheduleAccess && !timeMinutes) ||
                                        (scheduleAccess && (!startDate || !endDate))))
                            }
                            data-testid="button-submit"
                            size="sm"
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                        >
                            {isSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}